import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaStethoscope, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaCut,
  FaGut,
  FaShieldAlt,
  FaCalendarAlt,
  FaChartLine
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const LessonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const LessonCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  h3 {
    color: #0a4d68;
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: #f8fafc;
  padding: 2rem;
  border-radius: 15px;
  margin: 3rem 0;
  border-left: 4px solid #ffd966;
  
  .rating {
    color: #ffc107;
    margin-bottom: 1rem;
  }
  
  p {
    font-style: italic;
    color: #2c3e50;
    margin-bottom: 1rem;
  }
  
  .author {
    font-weight: 600;
    color: #0a4d68;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const GeneralSurgeryCoding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  
  const lessons = [
    {
      title: "Biopsies and Excisions",
      objectives: [
        "Differentiate between fine needle biopsy and needle core biopsy",
        "Assign codes for sentinel lymph node identification and biopsy",
        "Assign codes for bone marrow biopsy and aspiration",
        "Recognize when additional codes are needed for repairs"
      ]
    },
    {
      title: "Fluid Aspiration and Incision & Drainage",
      objectives: [
        "Discern between puncture aspiration and incision & drainage",
        "Distinguish perirectal from perianal abscess drainage",
        "Code for complex wound infections",
        "Identify appropriate modifiers for multiple procedures"
      ]
    },
    {
      title: "Procedures on the Gastrointestinal System",
      objectives: [
        "Differentiate between open and laparoscopic procedures",
        "Assign codes for incidental liver biopsy",
        "Align procedure descriptions to hemorrhoid procedure codes",
        "Code for colorectal resections and anastomosis"
      ]
    },
    {
      title: "Other General Surgery Procedures",
      objectives: [
        "Determine when to use code 49568 for mesh implantation",
        "Select codes for hysteroscopic dilation and curettage",
        "Identify codes for thyroidectomy procedures",
        "Code for hernia repairs with mesh"
      ]
    }
  ];

  const testimonials = [
    {
      rating: 5,
      text: "Long time coder of general surgery. I adored this because it took me out of my little box and into different procedures.",
      author: "Christine",
      date: "October 2024"
    },
    {
      rating: 5,
      text: "The explanations of how the procedures are performed is very helpful.",
      author: "Haugen Academy Learner",
      date: "August 2023"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          General Surgery Coding
          <span>Master CPT Surgery Coding</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive training in general surgery coding. Learn to code biopsies, excisions, 
          gastrointestinal procedures, hernia repairs, and more with confidence. [citation:3]
        </HeroDescription>
      </HeroSection>

      <Container>
        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              This course introduces you to procedures commonly performed by general surgeons in the 
              operating room. The course begins with coding and excision of lesions followed by fluid 
              aspiration and incision and drainage procedures. The course concludes with gastrointestinal 
              procedures and other general surgery procedures commonly seen in the OR. [citation:3]
            </p>
            <p>
              You'll be able to test your knowledge at the end of the course with the Code Red 
              Ultimate Challenge! Perfect for facility outpatient coders, professional fee coders, 
              and compliance professionals. [citation:3]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="General Surgery"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>Course Lessons</SectionTitle>
        <LessonGrid>
          {lessons.map((lesson, index) => (
            <LessonCard key={index}>
              <h3>{lesson.title}</h3>
              <List>
                {lesson.objectives.map((objective, idx) => (
                  <ListItem key={idx}>
                    <FaCheckCircle /> {objective}
                  </ListItem>
                ))}
              </List>
            </LessonCard>
          ))}
        </LessonGrid>

        <SectionTitle>What Our Students Say</SectionTitle>
        {testimonials.map((testimonial, index) => (
          <TestimonialCard key={index}>
            <div className="rating">
              {[...Array(testimonial.rating)].map((_, i) => (
                <FaStar key={i} color="#ffc107" />
              ))}
            </div>
            <p>"{testimonial.text}"</p>
            <div className="author">— {testimonial.author}, {testimonial.date}</div>
          </TestimonialCard>
        ))}

        <CTASection>
          <h3>Ready to Master General Surgery Coding?</h3>
          <p>Join our next batch and become an expert in surgical procedure coding</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default GeneralSurgeryCoding;